/* eslint-disable max-classes-per-file */
import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { AuthSelectors } from '../state/auth/auth.selectors';
import { UserSettingsStorage } from '../storage/user.settings.storage';

class RequestHeader extends HttpHeaders {
  appendIf(predicate: () => boolean, name: string, value: string) {
    if (predicate()) {
      return this.append(name, value);
    }
    return this;
  }

  override append(name: string, value: string | string[]): RequestHeader {
    return Object.assign(new RequestHeader(), super.append(name, value));
  }

  override set(name: string, value: string | string[]): RequestHeader {
    return Object.assign(new RequestHeader(), super.set(name, value));
  }

  withContentType(contentType: string) {
    return this.append('Content-Type', contentType);
  }
}

@Injectable({
  providedIn: 'root',
})
export class HttpAuthHeader {
  private contentType = 'application/json';
  private store = inject(Store);
  private configurationFacade = inject(ConfigurationFacade);
  private selectedFirmId = this.store.selectSignal(AuthSelectors.selectAuthFirmId);
  private uss = new UserSettingsStorage();

  public getHeader(additionalHeaders: Record<string, string> = {}) {
    const contentType = additionalHeaders?.['Content-Type'] || (this.contentType satisfies string);
    let headers = this.createHeaderWithBasicInfo().withContentType(contentType);
    for (const [key, value] of Object.entries(additionalHeaders)) {
      headers = headers.set(key, value);
    }
    return { headers, withCredentials: true };
  }

  public getHeaderWithoutBody(selectedTeams?: number[]) {
    const headers = this.createHeaderWithBasicInfo(selectedTeams).withContentType(this.contentType);
    return { headers, body: '', withCredentials: true };
  }

  public getHeaderWithResponseType(responseType: unknown) {
    const headers = this.getHeader();
    return { responseType, ...headers };
  }

  public getHeaderWithoutContentType(selectedTeams?: number[]) {
    const headers = this.createHeaderWithBasicInfo(selectedTeams);
    return { headers, withCredentials: !!this.getSessionCsrfToken() };
  }

  // eslint-disable-next-line complexity
  private createHeaderWithBasicInfo(selectedTeams?: number[]) {
    const csrfTokenName = this.configurationFacade.getValueFor('blauth').csrfTokenName;
    return new RequestHeader()
      .appendIf(() => !!this.getSessionJwt(), 'jwt', this.getSessionJwt() || '')
      .appendIf(() => !!this.getSessionCsrfToken(), csrfTokenName, this.getSessionCsrfToken() || '')
      .appendIf(() => !!this.selectedFirmId(), 'X-Firm-Id', (this.selectedFirmId() ?? '').toString())
      .appendIf(() => !!selectedTeams?.length, 'X-Teams', selectedTeams?.join(',') || '');
  }

  private getSessionJwt(): string {
    return this.uss.loadSetting(UserSettingsStorage.JWT, '');
  }

  private getSessionCsrfToken(): string {
    return this.uss.loadSetting(UserSettingsStorage.CSRF_TOKEN, '');
  }
}
